import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { JLPTLevel, Tag, Video, VideoCategory } from "@/types/video";
import {
  categoryBelongsToTab,
  getCategoryLabel,
  normalizeCategoryList,
} from "@/lib/category-config";

interface DbAudioRow {
  id: string;
  title: string;
  description: string | null;
  audio_url: string;
  thumbnail_url: string | null;
  duration: number | null;
  jlpt_level: string | null;
  category: string | null;
  categories?: string[] | null;
  series_id: string | null;
  series_order: number | null;
  view_count: number | null;
  like_count: number | null;
  created_at: string;
}

function formatDuration(seconds: number | null | undefined): string {
  const value = Number(seconds || 0);
  const mins = Math.floor(value / 60);
  const secs = value % 60;
  return `${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
}

function inferTopLevelCategory(categories: string[]): VideoCategory {
  if (categories.some((cat) => categoryBelongsToTab(cat, "textbook"))) return "textbook";
  if (categories.some((cat) => categoryBelongsToTab(cat, "news"))) return "news";
  if (categories.some((cat) => categoryBelongsToTab(cat, "podcast"))) return "podcast";
  return "all";
}

function buildTag(value: string): Tag {
  const colorMap: Record<string, Tag["color"]> = {
    textbook: "green",
    learning: "green",
    news: "orange",
    documentary: "orange",
    podcast: "purple",
    conversation: "blue",
    anime: "purple",
    drama: "purple",
    music: "blue",
    story: "blue",
    lifestyle: "purple",
  };

  return {
    label: getCategoryLabel(value),
    color: colorMap[value] || "blue",
  };
}

function mapAudioToVideo(row: DbAudioRow): Video {
  const categories = normalizeCategoryList([
    row.category,
    ...(Array.isArray(row.categories) ? row.categories : []),
  ]);

  return {
    id: row.id,
    episodeNumber: Number(row.series_order || 0),
    title: row.title,
    thumbnailUrl: row.thumbnail_url || "",
    videoUrl: row.audio_url,
    contentType: "audio",
    authorName: "dango",
    duration: formatDuration(row.duration),
    durationSeconds: Number(row.duration || 0),
    rating: 4.5,
    tags: categories.slice(0, 3).map(buildTag),
    jlptLevel: (row.jlpt_level || "N5") as JLPTLevel,
    category: inferTopLevelCategory(categories),
    categories,
    dbCategory: categories[0] || undefined,
    series: row.series_id || undefined,
    viewCount: row.view_count || 0,
    likeCount: row.like_count || 0,
    description: row.description || undefined,
    publishedAt: row.created_at,
    isLocked: false,
  };
}

export function useAudiosBySeries(seriesId: string) {
  return useQuery({
    queryKey: ["audios", "published", "series", seriesId],
    enabled: !!seriesId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("audios")
        .select("*")
        .eq("is_published", true)
        .eq("series_id", seriesId)
        .order("series_order", { ascending: true });

      if (error) throw error;

      return ((data || []) as DbAudioRow[]).map(mapAudioToVideo);
    },
  });
}
